/**
 * The Create Tenant form's section shell, and the Demo Connections preflight
 * that lives inside it.
 *
 * IN ITS OWN FILE SO IT CAN BE MOUNTED. `CreateTenantPage.jsx` imports
 * `react-router-dom`, which this repo's Jest cannot resolve, so nothing in that
 * file can be render-tested at all. The section that most needs looking at was
 * therefore the one section no test could see. Extracting it costs one import
 * and buys `DemoConnectionsReview.test.jsx`.
 */
import React from 'react';
import {
  AlertTriangle, Boxes, CalendarClock, CheckCircle2, GitBranch, HardDrive,
  Loader2, MessagesSquare, OctagonAlert, RefreshCw, Server, Sparkles,
} from 'lucide-react';

import { Button } from '../../../components/ui/button';
import StatusBadge from '../../../components/admin/StatusBadge';
import IdentityMark from '../../../design-system/identity/IdentityMark';
import ProviderMark from '../../../design-system/identity/ProviderMark';

/**
 * Purpose -> a FLAT MONOCHROME glyph. Not identity artwork: these are group
 * headings inside one section, drawn at 16px in `currentColor` with a single
 * stroke weight. Identity artwork is dimensional, sits on the Cluvik ramp and
 * is never rendered below 18px — using it here would tell an operator that
 * "Storage" is a place they can go to, and it is not.
 */
const PURPOSE_GLYPH = {
  ai: Sparkles,
  productivity: CalendarClock,
  storage: HardDrive,
  communications: MessagesSquare,
  developer: GitBranch,
  platform: Server,
  other: Boxes,
};

/** Severity -> the surface a banner is drawn on. Three tiers, never two:
 *  blocked (the profile cannot be applied), unknown (we could not look) and
 *  warning (the profile applies, something inside it is broken). Collapsing
 *  `unknown` into either of the others is the defect this area exists to
 *  expose — a failed read must never render as a clean bill of health. */
const SEVERITY_SURFACE = {
  blocked: 'border-destructive-text/40 bg-destructive/5',
  unknown: 'border-warning/40 bg-warning/10',
  warning: 'border-warning/30 bg-warning/5',
  ok: 'border-border bg-muted/30',
};

const SEVERITY_ICON = {
  blocked: { Icon: OctagonAlert, className: 'text-destructive-text' },
  unknown: { Icon: AlertTriangle, className: 'text-warning-text' },
  warning: { Icon: AlertTriangle, className: 'text-warning-text' },
  ok: { Icon: CheckCircle2, className: 'text-success' },
};

/**
 * A section on this form.
 *
 * THE GRADIENT TILES ARE GONE. Each card header used to carry a 3D-ish tile —
 * `from-sky-400 to-blue-500`, `from-amber-400 to-orange-500`, four more — none
 * of them on the Cluvik ramp, and none of them naming anything. Six invented
 * gradients on one screen is a second visual language, and a dimensional
 * treatment is how this system says IDENTITY: a place you can go to. A form
 * section is not a place. The glyph is now flat, monochrome and quiet, and the
 * page's one dimensional mark sits in the header where the identity actually
 * is.
 *
 * `mark` lets a section carry real identity artwork when it genuinely refers to
 * one — Demo Connections refers to the Connections area, and reusing that
 * area's mark is what makes the two screens read as the same subject.
 */
export const SectionCard = ({ icon: Icon, mark, title, subtitle, testId, children, action }) => (
  <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden" data-testid={testId}>
    <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-border bg-gradient-to-r from-muted/40 to-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {mark || (
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted shrink-0">
              <Icon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
    </div>
    <div className="px-5 sm:px-6 py-5">{children}</div>
  </div>
);

/**
 * What this tenant would be given, grouped by what it is FOR.
 *
 * Three things this had to get right and an earlier shape did not:
 *
 * 1. A FAILED READ IS NOT AN EMPTY PROFILE. `unknown` is its own banner, its
 *    own tone and its own words. The version that renders "no connections
 *    configured" when the runtime timed out is worse than no screen at all,
 *    because it hands the operator positive evidence for a thing nobody looked
 *    at.
 *
 * 2. REQUIRED FAILURE AND OPTIONAL WARNING ARE DIFFERENT SHAPES, not different
 *    shades. A profile that cannot be applied attaches NOTHING; a broken
 *    provider inside a profile that applies costs one connection. They are
 *    drawn as a full-width banner and as a per-row line respectively, so the
 *    difference survives being glanced at.
 *
 * 3. NO SECRET, ANYWHERE. Rows carry the secret's storage backend and nothing
 *    else — `describeSecret` in the shared module reduces it before it reaches
 *    here, and this component never sees `config` at all.
 *
 * Layout is a single stacked column on a phone and a two-track row from `sm`
 * up — the connection and its credential on the left, its health and the
 * lifecycle it attaches in on the right. A list rather than a table on purpose:
 * at 375px a table either scrolls sideways or throws its headers away, and a
 * status an operator has to scroll to find is one they will not read.
 */
export const DemoConnectionsReview = ({ demo, onRefresh }) => {
  const { Icon: SeverityIcon, className: severityClass } = SEVERITY_ICON[demo.severity] || SEVERITY_ICON.ok;
  const busy = demo.state === 'loading';

  return (
    <SectionCard
      title="Demo Connections review"
      subtitle="What this tenant would be given, before it is created."
      testId="demo-connections-review"
      /* Identity artwork at identity-lg: this section IS the Connections area,
         seen from here. Same mark as /connections/profiles, deliberately — a
         second silhouette would claim they are different subjects. */
      mark={<IdentityMark path="/connections/profiles" size="lg" className="mt-0.5 shrink-0" />}
      action={(
        /* An ACTION. Flat, monochrome, one stroke weight — never artwork. */
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={busy} data-testid="demo-connections-refresh">
          <RefreshCw className={`mr-2 h-4 w-4 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" />
          Re-check
        </Button>
      )}
    >
      {/* ── the profile being applied ──────────────────────────────── */}
      {demo.profile && (
        <div
          className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-border bg-muted/30 px-4 py-3"
          data-testid="demo-connections-profile"
        >
          <span className="text-sm font-medium text-foreground">{demo.profile.label || demo.profile.id}</span>
          <StatusBadge status={String(demo.profile.environment || '').toLowerCase()} label={demo.profile.environment} dot={false} />
          <span className="font-mono text-xs text-muted-foreground break-all">{demo.profile.id}</span>
        </div>
      )}

      {/* There was a warning banner here saying the profile reviewed was not
          provably the profile attached. It is gone because the statement is no
          longer true: `describeDemoConnections` now previews the profile the
          environment registry names, which is the id `create_tenant` resolves,
          so the chip above IS what gets attached. A banner qualifying a
          guarantee that now holds would teach an operator to distrust it. */}

      {/* ── the one headline, at the severity that was measured ─────── */}
      <div
        className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${SEVERITY_SURFACE[demo.severity] || SEVERITY_SURFACE.ok}`}
        data-testid={`demo-connections-severity-${demo.severity}`}
        role={demo.severity === 'blocked' ? 'alert' : undefined}
      >
        {busy
          ? <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
          : <SeverityIcon className={`mt-0.5 h-5 w-5 shrink-0 ${severityClass}`} aria-hidden="true" />}
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{demo.headline}</p>
          {demo.detail && <p className="mt-1 text-sm text-muted-foreground break-words">{demo.detail}</p>}

          {/* REQUIRED: the profile cannot be applied at all.
              NESTED INSIDE THE HEADLINE, and TONED BY SEVERITY. Two mistakes
              were visible the first time this was drawn:
                · blocked rendered a red banner and then a second, almost
                  identical red box under it, so the screen said the same
                  thing twice and neither instance carried more weight;
                · unknown rendered its consequence in DESTRUCTIVE red, which
                  states a failure nobody measured — the precise inversion of
                  what "we could not look" means.
              One block, one tone, taken from the severity that was actually
              measured. */}
          {demo.blocking.length > 0 && (
            <ul className="mt-3 space-y-2 border-t border-border/60 pt-3" data-testid="demo-connections-blocking">
              {demo.blocking.map((b) => (
                <li key={b.code}>
                  <p className={`text-sm font-semibold ${severityClass}`}>{b.title}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground break-words">{b.detail}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── the connections themselves, grouped by purpose ──────────── */}
      {demo.groups.length > 0 && (
        <div className="mt-5 space-y-5" data-testid="demo-connections-groups">
          {demo.groups.map((group) => {
            const Glyph = PURPOSE_GLYPH[group.key] || Boxes;
            return (
              <section key={group.key} data-testid={`demo-connections-group-${group.key}`}>
                <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {/* Flat monochrome, 16px. A category marker, not artwork. */}
                  <Glyph className="h-4 w-4" aria-hidden="true" />
                  {group.label}
                  <span className="font-normal normal-case tracking-normal">· {group.rows.length}</span>
                </h4>
                <ul className="mt-2 divide-y divide-border rounded-xl border border-border">
                  {group.rows.map((row) => (
                    <li
                      key={`${row.provider}:${row.connection || 'provider'}`}
                      className="grid grid-cols-1 gap-x-4 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start"
                      data-testid={`demo-connection-${row.connection || row.provider}`}
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        {/* THE PROVIDER'S MARK — a Cluvik stand-in, because
                            this repository holds no licensed provider artwork
                            and will not redraw a brand mark from memory. See
                            ProviderMark.jsx; `data-artwork` says which it is.
                            Decorative here: the provider's name is written one
                            element away and announcing it twice is worse than
                            announcing it once. */}
                        <ProviderMark
                          provider={row.provider}
                          label={row.providerLabel}
                          size="sm"
                          className="mt-0.5 shrink-0"
                        />
                        <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{row.label}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {row.providerLabel}
                          {' · '}
                          {row.credentialLabel}
                          {row.secretLabel ? ` · ${row.secretLabel}` : ''}
                        </p>
                        {row.providerNotReported && (
                          <p className="mt-1 text-xs text-warning-text">
                            The runtime named this connection and sent nothing about the provider
                            behind it. Whether a credential exists was not measured.
                          </p>
                        )}
                        {row.lastError && (
                          <p className="mt-1 text-xs text-destructive-text break-words">{row.lastError}</p>
                        )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                        {/* Health: what the PROVIDER last said. */}
                        <StatusBadge status={row.badgeStatus} label={row.healthLabel} />
                        {/* Lifecycle: what the attach will WRITE. Two axes,
                            two controls — "not configured" and "failing" call
                            for opposite next moves and must not share a chip. */}
                        <span
                          className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
                          title="The lifecycle the created connection row starts in"
                        >
                          attaches {row.lifecycleLabel.toLowerCase()}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      {/* ── OPTIONAL: providers inside an applicable profile ────────── */}
      {demo.warnings.length > 0 && (
        <details className="mt-4 rounded-xl border border-warning/30 bg-warning/5 px-4 py-3" data-testid="demo-connections-warnings">
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            {demo.warnings.length} connection{demo.warnings.length === 1 ? '' : 's'} will not work
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              the rest are still attached
            </span>
          </summary>
          <ul className="mt-3 space-y-2.5">
            {demo.warnings.map((w, i) => (
              <li key={`${w.code}:${w.connection}:${i}`}>
                <p className="text-sm text-foreground">{w.title}</p>
                <p className="text-xs text-muted-foreground break-words">{w.detail}</p>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* ── what the profile deliberately does not cover ──────────────
          ONE LINE PER CATEGORY, never a silent omission and never a promise.
          A category left off the screen reads as "there is no such thing";
          a category listed with member names and no state reads as "these
          are coming". Each row says the category, what the demo catalogue
          would put in it, and that this profile has none of it. */}
      {demo.absent.length > 0 && (
        <section className="mt-5" data-testid="demo-connections-absent">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Not in this profile
            <span className="ml-2 font-normal normal-case tracking-normal">· {demo.absent.length}</span>
          </h4>
          <ul className="mt-2 divide-y divide-border rounded-xl border border-dashed border-border">
            {demo.absent.map((a) => {
              const Glyph = PURPOSE_GLYPH[a.key] || Boxes;
              return (
                <li
                  key={a.key}
                  className="grid grid-cols-1 gap-x-4 gap-y-1 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                  data-testid={`demo-connections-absent-${a.key}`}
                >
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      {/* Flat monochrome, 16px — the same category marker the
                          present groups use, so absence reads as the same kind
                          of thing as presence rather than as a different list. */}
                      <Glyph className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {a.label}
                    </p>
                    {a.members && (
                      /* "Covers", not "Would carry". The first draft promised:
                         it read as though these were on their way. Four of the
                         six categories have no member in the demo catalogue at
                         all, so nothing here is coming. */
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Covers {a.members}.
                      </p>
                    )}
                  </div>
                  {/* PROFILE-SCOPED, deliberately. The absent set is derived
                      from what this profile served, so "not in the catalogue"
                      would be a claim this render cannot make about every
                      category — Storage would be wrong the day a profile
                      carries no S3 credential. */}
                  <span
                    className="justify-self-start rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground sm:justify-self-end"
                    title="No connection in this category is part of the selected profile"
                  >
                    Nothing in this profile
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">
            No credential for these categories is in this profile, so this tenant receives
            nothing from them. Nothing here is loading and nothing is pending — the demo
            catalogue is a closed set, and a category with no member has nothing to attach.
            Listed rather than omitted: a category left off the screen reads as one that
            does not exist, and a category listed without this line reads as one that is
            on its way.
          </p>
        </section>
      )}

      {/* ── the limit of what this screen can know ──────────────────── */}
      {demo.rows.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="demo-connections-scope-note">
          Health here is the profile&apos;s own last provider check, shared by every tenant on
          this profile. Whether Cluvik&apos;s own path through a connection works end to end is
          not measured before creation and is not claimed here.
        </p>
      )}
    </SectionCard>
  );
};

